import OverflowMenuHorizontal16 from '@carbon/icons-react/es/overflow-menu--horizontal/16';
import OverflowMenuVertical16 from '@carbon/icons-react/es/overflow-menu--vertical/16';
import { Tooltip } from 'antd';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import {
  Menu,
  MenuItem,
  MenuLink,
  SubMenu,
  SubMenuItem,
} from './dynamic-menu.style';
import { LeftNavMenuItem, LeftNavMenus, LeftNavMenuType } from './types';

const PerMenuItemHeight = 48;
const renderMenuItem = (
  item: LeftNavMenuItem,
  translate: ReturnType<typeof useTranslate>,
) => (
  <MenuItem key={item.key}>
    <Tooltip
      placement="right"
      title={translate(`menu.label.${item.key}` as TranslationKey)}
    >
      <MenuLink
        to={item.path}
        aria-label={translate('menu.aria_label' as TranslationKey, {
          title: translate(`menu.label.${item.key}` as TranslationKey),
        })}
      >
        {item.icon}
      </MenuLink>
    </Tooltip>
  </MenuItem>
);
const renderSubMenuItem = (
  item: LeftNavMenuItem,
  translate: ReturnType<typeof useTranslate>,
) => (
  <SubMenuItem key={item.key}>
    <MenuLink
      to={item.path}
      aria-label={translate('menu.aria_label' as TranslationKey, {
        title: translate(`menu.label.${item.key}` as TranslationKey),
      })}
    >
      {item.icon}
      <FormattedMessage id={`menu.label.${item.key}`} />
    </MenuLink>
  </SubMenuItem>
);
const renderMenus = (
  menus: LeftNavMenus,
  translate: ReturnType<typeof useTranslate>,
) =>
  menus.map((item) => {
    return item.type === LeftNavMenuType.Leaf ? (
      renderMenuItem(item, translate)
    ) : item.children.length === 1 ? (
      renderMenuItem(item.children[0], translate)
    ) : (
      <SubMenu
        key={item.key}
        popupClassName="left_nav_sub_menu"
        icon={
          <>
            {item.icon}
            <OverflowMenuVertical16 />
          </>
        }
      >
        {item.children.map((childItem) =>
          renderSubMenuItem(childItem, translate),
        )}
      </SubMenu>
    );
  });
const renderSubMenus = (
  menus: LeftNavMenus,
  translate: ReturnType<typeof useTranslate>,
) =>
  menus.map((item) => {
    return item.type === LeftNavMenuType.Leaf
      ? renderSubMenuItem(item, translate)
      : item.children.map((childItem) =>
          renderSubMenuItem(childItem, translate),
        );
  });

type DynamicMenuProps = {
  className?: string;
  items: LeftNavMenus;
  selectedKey?: string;
  availableSpace: number;
};
export const DynamicMenu: FC<DynamicMenuProps> = ({
  className,
  items,
  selectedKey,
  availableSpace,
}) => {
  const translate = useTranslate();
  const numberOfMenuItemCanBeShown = Math.max(
    (availableSpace - PerMenuItemHeight) / PerMenuItemHeight,
    2,
  );
  const showableMenus = useMemo(
    () =>
      numberOfMenuItemCanBeShown < items.length
        ? items.slice(0, numberOfMenuItemCanBeShown - 1)
        : items,
    [items, numberOfMenuItemCanBeShown],
  );
  const moreMenus = useMemo(
    () =>
      numberOfMenuItemCanBeShown < items.length
        ? items.slice(numberOfMenuItemCanBeShown - 1)
        : [],
    [items, numberOfMenuItemCanBeShown],
  );

  return (
    <Menu
      className={className}
      mode="vertical"
      selectedKeys={[selectedKey].filter(Boolean).map((x) => x!)}
    >
      {renderMenus(showableMenus, translate)}
      {moreMenus.length > 0 ? (
        <SubMenu
          key="more-menu"
          icon={<OverflowMenuHorizontal16 />}
          popupClassName="left_nav_sub_menu"
        >
          {renderSubMenus(moreMenus, translate)}
        </SubMenu>
      ) : null}
    </Menu>
  );
};
